import React, { useState } from "react";
import { createPortal } from "react-dom";

const PALETTE = [
	"#6366f1",
	"#f59e0b",
	"#22c55e",
	"#06b6d4",
	"#ec4899",
	"#ef4444",
	"#8b5cf6",
	"#14b8a6",
	"#f97316",
	"#64748b",
];

export default function CategoriesModal({
	categories,
	saving,
	onClose,
	onCreate,
	onUpdate,
	onDelete,
	themeVars,
	overlayBg,
}) {
	const custom = (categories || []).filter((c) => !c.isSystem);
	const system = (categories || []).find((c) => c.isSystem);
	const [name, setName] = useState("");
	const [color, setColor] = useState(PALETTE[0]);
	const [editingId, setEditingId] = useState(null);
	const [editName, setEditName] = useState("");
	const [editColor, setEditColor] = useState(PALETTE[0]);

	const submitNew = (e) => {
		e.preventDefault();
		if (!name.trim() || saving) return;
		onCreate({ name: name.trim(), color });
		setName("");
		setColor(PALETTE[0]);
	};

	return createPortal(
		<div
			className="tailwick-app fixed inset-0 z-[1300] flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-hidden"
			style={{ ...themeVars, background: overlayBg, color: "var(--app-text)" }}
			onClick={onClose}
			role="presentation"
		>
			<div
				className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-[var(--app-surface)] border border-[var(--app-border)] shadow-xl max-h-[92vh] flex flex-col"
				onClick={(e) => e.stopPropagation()}
				role="dialog"
			>
				<div className="flex items-center justify-between px-5 py-4 border-b border-[var(--app-border)] shrink-0">
					<h3 className="text-base font-semibold m-0">Categorias</h3>
					<button
						type="button"
						onClick={onClose}
						className="w-8 h-8 rounded-lg border-0 bg-transparent cursor-pointer text-[var(--app-text-muted)]"
					>
						✕
					</button>
				</div>

				<div className="p-5 flex flex-col gap-4 overflow-y-auto min-h-0">
					{system ? (
						<div className="flex items-center gap-2 rounded-xl border border-[var(--app-border)] bg-[var(--app-body-bg)] px-3 py-2">
							<span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: system.color }} />
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium m-0 truncate">{system.name}</p>
								<p className="text-[11px] m-0 text-[var(--app-text-muted)]">Fixa do servidor — somente leitura</p>
							</div>
						</div>
					) : null}

					<form onSubmit={submitNew} className="flex flex-col gap-2">
						<label className="text-xs font-medium text-[var(--app-text-muted)]">Nova categoria</label>
						<input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="Ex: RH, Comercial, Treinamentos"
							className="w-full px-3 py-2 rounded-xl text-sm outline-none bg-[var(--app-body-bg)] text-[var(--app-text)] border border-[var(--app-border)]"
						/>
						<div className="flex flex-wrap gap-1.5">
							{PALETTE.map((c) => (
								<button
									key={c}
									type="button"
									onClick={() => setColor(c)}
									className="w-6 h-6 rounded-full border-2 cursor-pointer p-0"
									style={{
										background: c,
										borderColor: color === c ? "var(--app-text)" : "transparent",
									}}
									aria-label={c}
								/>
							))}
						</div>
						<button
							type="submit"
							disabled={saving || !name.trim()}
							className="self-start px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-indigo-500 border-0 cursor-pointer disabled:opacity-50"
						>
							Adicionar
						</button>
					</form>

					<div className="flex flex-col gap-2">
						<p className="text-xs font-medium m-0 text-[var(--app-text-muted)]">Suas categorias</p>
						{custom.length === 0 ? (
							<p className="text-sm m-0 text-[var(--app-text-muted)]">Nenhuma categoria própria ainda.</p>
						) : (
							custom.map((cat) => (
								<div
									key={cat.id}
									className="flex items-center gap-2 rounded-xl border border-[var(--app-border)] bg-[var(--app-body-bg)] px-3 py-2"
								>
									{editingId === cat.id ? (
										<div className="flex-1 min-w-0 flex flex-col gap-2">
											<input
												value={editName}
												onChange={(e) => setEditName(e.target.value)}
												className="w-full px-2 py-1.5 rounded-lg text-sm outline-none bg-[var(--app-surface)] text-[var(--app-text)] border border-[var(--app-border)]"
											/>
											<div className="flex flex-wrap gap-1.5">
												{PALETTE.map((c) => (
													<button
														key={c}
														type="button"
														onClick={() => setEditColor(c)}
														className="w-5 h-5 rounded-full border-2 cursor-pointer p-0"
														style={{
															background: c,
															borderColor: editColor === c ? "var(--app-text)" : "transparent",
														}}
													/>
												))}
											</div>
											<div className="flex gap-2">
												<button
													type="button"
													disabled={saving || !editName.trim()}
													onClick={() => {
														onUpdate(cat.id, { name: editName.trim(), color: editColor });
														setEditingId(null);
													}}
													className="text-xs font-semibold text-indigo-500 border-0 bg-transparent cursor-pointer"
												>
													Salvar
												</button>
												<button
													type="button"
													onClick={() => setEditingId(null)}
													className="text-xs text-[var(--app-text-muted)] border-0 bg-transparent cursor-pointer"
												>
													Cancelar
												</button>
											</div>
										</div>
									) : (
										<>
											<span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.color }} />
											<span className="text-sm flex-1 truncate">{cat.name}</span>
											<button
												type="button"
												onClick={() => {
													setEditingId(cat.id);
													setEditName(cat.name);
													setEditColor(cat.color || PALETTE[0]);
												}}
												className="text-xs text-[var(--app-text-muted)] border-0 bg-transparent cursor-pointer"
											>
												Editar
											</button>
											<button
												type="button"
												onClick={() => onDelete(cat)}
												className="text-xs text-red-500 border-0 bg-transparent cursor-pointer"
											>
												Excluir
											</button>
										</>
									)}
								</div>
							))
						)}
					</div>
				</div>
			</div>
		</div>,
		document.body
	);
}
